import { ptBrCommon } from '@/lib/pt-br/messages';
import type { PopularProductQueryOptions, Product, ProductPaginator, ProductQueryOptions, QuestionPaginator, QuestionQueryOptions, BestSellingProductQueryOptions } from '@/types';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
} from 'react-query';
import client from './client';
import { API_ENDPOINTS } from './client/api-endpoints';
import { mapPaginatorData } from '@/framework/utils/data-mappers';
import { formatProductsArgs } from '@/framework/utils/format-products-args';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { extractRecipeValidityDaysFromResponse } from '@/lib/utils/product';

export function useProducts(options?: Partial<ProductQueryOptions>) {
  const { locale } = useRouter();

  const formattedOptions = {
    ...formatProductsArgs(options),
    language: locale,
    page: 1,
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery<ProductPaginator, Error>(
    [API_ENDPOINTS.PRODUCTS, formattedOptions],
    ({ queryKey, pageParam }) =>
      client.products.all(Object.assign({}, queryKey[1], pageParam)),
    {
      getNextPageParam: ({ current_page, last_page }) =>
        last_page > current_page && { page: current_page + 1 },
    },
  );

  function handleLoadMore() {
    fetchNextPage();
  }

  return {
    products: data?.pages?.flatMap((page) => page.data) ?? [],
    paginatorInfo: Array.isArray(data?.pages)
      ? mapPaginatorData(data?.pages[data.pages.length - 1])
      : null,
    isLoading,
    error,
    isFetching,
    isLoadingMore: isFetchingNextPage,
    loadMore: handleLoadMore,
    hasMore: Boolean(hasNextPage),
  };
}

export const usePopularProducts = (
  options?: Partial<PopularProductQueryOptions>,
) => {
  const { locale } = useRouter();

  const formattedOptions = {
    ...options,
    language: locale,
  };

  const { data, isLoading, error } = useQuery<Product[], Error>(
    [API_ENDPOINTS.PRODUCTS_POPULAR, formattedOptions],
    ({ queryKey }) =>
      client.products.popular(queryKey[1] as PopularProductQueryOptions),
  );

  return {
    products: data ?? [],
    isLoading,
    error,
  };
};

export const useBestSellingProducts = (
  options?: Partial<BestSellingProductQueryOptions>,
) => {
  const { locale } = useRouter();

  const formattedOptions = {
    ...options,
    language: locale,
  };

  const { data, isLoading, error } = useQuery<Product[], Error>(
    [API_ENDPOINTS.BEST_SELLING_PRODUCTS, formattedOptions],
    ({ queryKey }) =>
      client.products.bestSelling(
        queryKey[1] as BestSellingProductQueryOptions,
      ),
  );

  return {
    products: data ?? [],
    isLoading,
    error,
  };
};

export function useProduct({ slug, id }: { slug: string; id?: string }) {
  const { locale: language } = useRouter();

  const {
    data: product,
    isLoading,
    error,
  } = useQuery<Product, Error>(
    [API_ENDPOINTS.PRODUCTS, { slug, language }],
    () => client.products.get({ slug, language }),
  );

  const { data: pricesData } = useQuery(
    [API_ENDPOINTS.PRODUCTS_PRICE, slug],
    async () => {
      const response = await client.productPrice.get({
        search: `product.id:${id}`,
      });
      return response;
    },
  );

  const enhancedProduct = product
    ? {
        ...product,
        price: pricesData?.[0]?.price ?? null,
        sale_price: pricesData?.[0]?.sale_price ?? null,
        prices: pricesData ?? [],
      }
    : null;

  return {
    product: enhancedProduct,
    isLoading,
    error,
  };
}

export function useProductPrice({ product_id }: { product_id: string | number }) {
  const {
    data: pricesData,
    isLoading,
    error,
  } = useQuery(
    [API_ENDPOINTS.PRODUCTS_PRICE, product_id],
    async () => {
      const response = await client.productPrice.get({
        search: `product.id:${product_id}`,
        include: 'price_channel',
      });
      return response;
    },
    { enabled: !!product_id },
  );

  const primaryChannelPrice = pricesData?.find(
    (price: any) => price.price_channel_id === 1,
  );

  return {
    data: {
      price: primaryChannelPrice?.price ?? null,
      sale_price: primaryChannelPrice?.sale_price ?? null,
      prices: pricesData ?? [],
    },
    isLoading,
    error,
  };
}

export function useProductCharacteristics({ id }: { id: string | number }) {
  const {
    data: characteristicsData,
    isLoading,
    error,
  } = useQuery([API_ENDPOINTS.CHARACTERISTICS, id], async () => {
    const response = await client.productCharacteristics.get({
      id,
    });
    return response;
  });

  return {
    data: characteristicsData,
    isLoading,
    error,
  };
}

export function useBuyTogetherProduct(productId: number | undefined) {
  const { data, isLoading, error } = useQuery(
    ['buy-together', productId],
    () => client.products.buyTogether(productId!),
    {
      enabled: !!productId,
      staleTime: 60_000,
    },
  );

  return {
    data: data ?? null,
    isLoading,
    error,
  };
}

/**
 * Fetches characteristics for multiple products and extracts recipe validity days
 * @param productIds - Array of product IDs to fetch characteristics for
 * @returns Object with maxRecipeValidityDays and isLoading
 */
export function useProductsRecipeValidityDays(productIds: (string | number)[]) {
  // In react-query v3, useQueries takes an array directly
  const queries = useQueries(
    productIds.map((productId) => ({
      queryKey: [API_ENDPOINTS.CHARACTERISTICS, productId],
      queryFn: async () => {
        const response = await client.productCharacteristics.get({
          id: productId,
        });
        return response;
      },
      enabled: !!productId,
    })),
  );

  const isLoading = queries.some((query) => query.isLoading);

  // Extract recipe validity days from all responses and find the maximum
  const maxRecipeValidityDays = useMemo(() => {
    let maxDays: number | undefined;

    queries.forEach((query) => {
      if (query.data) {
        const days = extractRecipeValidityDaysFromResponse(
          query.data as Record<string, { characteristics: any[] }>,
        );
        if (days !== undefined) {
          if (maxDays === undefined || days > maxDays) {
            maxDays = days;
          }
        }
      }
    });

    return maxDays;
  }, [queries]);

  return {
    maxRecipeValidityDays,
    isLoading,
  };
}

export function useQuestions(options?: Partial<QuestionQueryOptions>) {
  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useQuery<QuestionPaginator, Error>(
    [API_ENDPOINTS.PRODUCTS_QUESTIONS, options],
    ({ queryKey }) =>
      client.products.questions(
        Object.assign({}, queryKey[1] as QuestionQueryOptions),
      ),
    {
      keepPreviousData: true,
    },
  );
  return {
    questions: response?.data ?? [],
    paginatorInfo: mapPaginatorData(response),
    isLoading,
    error,
    isFetching,
  };
}

export function useCreateFeedback() {
const queryClient = useQueryClient();
  const { mutate: createFeedback, isLoading } = useMutation(
    client.products.createFeedback,
    {
      onSuccess: (res) => {
        toast.success(`${"Feedback Enviado"}`);
        queryClient.refetchQueries(API_ENDPOINTS.PRODUCTS_QUESTIONS);
        queryClient.refetchQueries(API_ENDPOINTS.PRODUCTS_REVIEWS);
      },
    },
  );
  return {
    createFeedback,
    isLoading,
  };
}

export function useCreateAbuseReport() {
const { closeModal } = useModalAction();
  const { mutate: createAbuseReport, isLoading } = useMutation(
    client.products.createAbuseReport,
    {
      onSuccess: () => {
        toast.success(`${"Relatório de abuso enviado com sucesso"}`);
      },
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};

        toast.error(`${ptBrCommon(data?.message)}`);
      },
      onSettled: () => {
        closeModal();
      },
    },
  );
  return {
    createAbuseReport,
    isLoading,
  };
}

export function useCreateQuestion() {
const { closeModal } = useModalAction();
  const queryClient = useQueryClient();
  const { mutate: createQuestion, isLoading } = useMutation(
    client.products.createQuestion,
    {
      onSuccess: () => {
        toast.success(`${"Pergunta enviada com sucesso. Aguarde a resposta do vendedor."}`);
      },
      onError: (error) => {
        const {
          response: { data },
        }: any = error ?? {};

        toast.error(`${ptBrCommon(data?.message)}`);
      },
      onSettled: () => {
        queryClient.refetchQueries(API_ENDPOINTS.PRODUCTS_QUESTIONS);
        closeModal();
      },
    },
  );
  return {
    createQuestion,
    isLoading,
  };
}
